// Agents mode's query options (M3-08): one place for the keys and SDK calls of runs, approvals,
// budgets, a DE's budget, composition and verified rate, a run's whole decision log (DC-07), the
// caller's approvals count (AGT-UI-2), and the wake-up events, so each screen's calls are
// visible from what it mounts. Keys start `["company", co, <area>]`: a write invalidates its
// area, and a change ping on a run, approval or budget (live/change-pings.ts) does the same.
//
// Approvals (M3-03) and budgets (M3-04) have no handler on every API yet: until their route is
// registered it answers 404, and these reads turn that into `null` (no toast), so the screen says
// "not live on this server yet" instead of failing.
import { ApiError } from "@companyos/sdk";
import { infiniteQueryOptions, type QueryClient, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { pendingAsNull } from "../work/work-queries";

export const agentKeys = {
  runs: (co: string) => ["company", co, "runs"] as const,
  runList: (co: string, filter: RunFilter) =>
    ["company", co, "runs", "list", filter.state ?? "all", filter.membership_id ?? "all"] as const,
  run: (co: string, id: string) => ["company", co, "runs", "run", id] as const,
  runDecisions: (co: string, id: string) =>
    ["company", co, "runs", "run", id, "decisions"] as const,
  runTelemetry: (co: string, id: string) =>
    ["company", co, "runs", "run", id, "telemetry"] as const,
  alerts: (co: string) => ["company", co, "monitoring", "alerts"] as const,
  alertList: (co: string, filter: { state: string; kind?: string | undefined }) =>
    ["company", co, "monitoring", "alerts", filter.state, filter.kind ?? "all"] as const,
  fleet: (co: string) => ["company", co, "monitoring", "fleet"] as const,
  budgetIncidents: (co: string, filter: BudgetIncidentFilter) =>
    [
      "company",
      co,
      "monitoring",
      "budget-incidents",
      filter.de ?? "all",
      filter.period ?? "all",
      filter.state,
    ] as const,
  approvals: (co: string) => ["company", co, "approvals"] as const,
  approvalList: (co: string, filter: ApprovalFilter) =>
    ["company", co, "approvals", "list", filter.state ?? "all", filter.approver ?? "all"] as const,
  approval: (co: string, id: string) => ["company", co, "approvals", "one", id] as const,
  myApprovals: (co: string) => ["company", co, "approvals", "mine"] as const,
  myApprovalsCount: (co: string) => ["company", co, "approvals", "count"] as const,
  budgets: (co: string) => ["company", co, "budgets"] as const,
  budgetList: (co: string) => ["company", co, "budgets", "list"] as const,
  budget: (co: string, id: string) => ["company", co, "budgets", "one", id] as const,
  deBudget: (co: string, id: string) => ["company", co, "de", id, "budget"] as const,
  deAgents: (co: string, id: string) => ["company", co, "de", id, "agents"] as const,
  deVerifiedRate: (co: string, id: string) => ["company", co, "de", id, "verified-rate"] as const,
  wakeups: (co: string) => ["company", co, "wakeups"] as const,
  wakeupInbox: (co: string) => ["company", co, "wakeups", "inbox"] as const,
  // DOCS-KB: an agent's knowledge sources (a DE's or a crew's), and who reads a page.
  knowledge: (co: string) => ["company", co, "knowledge"] as const,
  agentKnowledge: (co: string, membershipId: string) =>
    ["company", co, "knowledge", "agent", membershipId] as const,
  pageReaders: (co: string, pageId: string) =>
    ["company", co, "knowledge", "readers", pageId] as const,
};

/** The docs, spaces and page kinds an agent reads (base plan: shows with `agents` off). */
export const knowledgeQuery = (api: Api, co: string, membershipId: string) =>
  queryOptions({
    queryKey: agentKeys.agentKnowledge(co, membershipId),
    queryFn: () => api.client.agentKnowledge.list({ params: { co, membership_id: membershipId } }),
  });

/** The agents whose knowledge covers a page ("Used by"). */
export const pageReadersQuery = (api: Api, co: string, pageId: string) =>
  queryOptions({
    queryKey: agentKeys.pageReaders(co, pageId),
    queryFn: () => api.client.pages.readers({ params: { co, id: pageId } }),
  });

/** After a knowledge add or remove: every agent's list and every page's readers. */
export function invalidateKnowledge(qc: QueryClient, co: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: agentKeys.knowledge(co) });
}

/** Rows a page of the runs and approvals lists ("Load more" after it). */
export const AGENTS_PAGE = 50;
/** The roster reads the latest runs once and derives each DE's status and spend from them. */
export const ROSTER_RUNS = 100;

export type RunFilter = { state?: RunState; membership_id?: string };
export type ApprovalFilter = {
  state?: ApprovalState;
  approver?: "me";
  /** AGENT-REPORTING: one kind only (the KPI reviews: `kpi_value`). */
  kind?: ApprovalKind;
};

type RunState = NonNullable<Parameters<Api["client"]["runs"]["list"]>[0]["query"]>["state"];
type ApprovalState = NonNullable<
  Parameters<Api["client"]["approvals"]["list"]>[0]["query"]
>["state"];
type ApprovalKind = NonNullable<Parameters<Api["client"]["approvals"]["list"]>[0]["query"]>["kind"];

const next = (last: { next_cursor: string | null }) => last.next_cursor ?? undefined;

export const runsQuery = (api: Api, co: string, filter: RunFilter = {}, limit = AGENTS_PAGE) =>
  infiniteQueryOptions({
    queryKey: [...agentKeys.runList(co, filter), limit],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.runs.list({
        params: { co },
        query: {
          limit,
          ...(filter.state ? { state: filter.state } : {}),
          ...(filter.membership_id ? { membership_id: filter.membership_id } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
  });

/** ACTIVITY: the feed's filters (Who: a DE, an AG or a team; State). */
export type ActivityFilter = {
  who?: { kind: "de" | "ag" | "team"; id: string } | undefined;
  state?: "live" | "waiting" | "done" | "failed" | undefined;
  /** The child runs of one run (any state). */
  parent?: string | undefined;
};

/** The viewer's start of today (the toolbar's "today"). */
function dayStart(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * ACTIVITY: Workforce › Activity (`workforce.activity`): live runs on the first page (`now`), the
 * rest newest first. Under `runs`, so a stop, a decision or a run ping refreshes it; the Now group
 * is also refetched every 30 s (design §5: no live pings in phase 1).
 */
/** How often the feed, expanded child lists and open trails are read again (no live pings). */
export const ACTIVITY_REFRESH_MS = 30_000;

export const activityQuery = (api: Api, co: string, filter: ActivityFilter = {}) =>
  infiniteQueryOptions({
    queryKey: [
      ...agentKeys.runs(co),
      "activity",
      filter.who?.kind ?? "all",
      filter.who?.id ?? "all",
      filter.state ?? "all",
      filter.parent ?? "top",
    ],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.workforce.activity({
        params: { co },
        query: {
          limit: AGENTS_PAGE,
          day_start: dayStart(),
          ...(filter.who ? { who_kind: filter.who.kind, who_id: filter.who.id } : {}),
          ...(filter.state ? { state: filter.state } : {}),
          ...(filter.parent ? { parent_run_id: filter.parent } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: next,
    // Expanded child lists too: their rows (state, last line) drive their open trails.
    refetchInterval: ACTIVITY_REFRESH_MS,
  });

/** Events a page of a run's trail reads (`events.list`'s maximum). */
export const RUN_EVENTS_PAGE = 200;

/**
 * ACTIVITY: the events one run wrote, oldest first, page by page on the `after_seq` cursor (its
 * trail; `events.list` is for managers). A short page is the last.
 */
export const runEventsQuery = (api: Api, co: string, runId: string) =>
  infiniteQueryOptions({
    queryKey: [...agentKeys.run(co, runId), "events"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.events.list({
        params: { co },
        query: {
          actor_run_id: runId,
          limit: RUN_EVENTS_PAGE,
          ...(pageParam ? { after_seq: Number(pageParam) } : {}),
        },
      }),
    getNextPageParam: (last) =>
      last.data.length < RUN_EVENTS_PAGE ? undefined : (last.next_cursor ?? undefined),
  });

export const runQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.run(co, id),
    queryFn: () => api.client.runs.get({ params: { co, id } }),
  });

/**
 * MONITORING-P1: what the DE's host reported for a run (`runs.telemetry`): its timeline events,
 * job cards, stop requests and alerts. Under the run's key, so a run write refreshes it too. A
 * server without the route (404) or a refusal (403) leaves the page as it was, no toast.
 */
export const runTelemetryQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.runTelemetry(co, id),
    queryFn: () => api.client.runs.telemetry({ params: { co, id } }),
    meta: { quietStatuses: [403, 404] },
    retry: false,
  });

export type AlertFilter = {
  state: "active" | "resolved" | "all";
  kind?: AlertKind | undefined;
};
type AlertKind = NonNullable<
  NonNullable<Parameters<Api["client"]["monitoring"]["alerts"]["list"]>[0]["query"]>["kind"]
>;

/** MONITORING-P1: the company's monitoring alerts (managers), `active` = open and acked. */
export const monitorAlertsQuery = (api: Api, co: string, filter: AlertFilter) =>
  queryOptions({
    queryKey: agentKeys.alertList(co, filter),
    queryFn: () =>
      api.client.monitoring.alerts.list({
        params: { co },
        query: { state: filter.state, limit: 200, ...(filter.kind ? { kind: filter.kind } : {}) },
      }),
  });

/**
 * MONITORING-P2: the fleet (managers): per DE seat, where its bridge runs, the bridge version,
 * the last heartbeat and event, telemetry on/off and open alerts. Refreshed every minute.
 */
export const fleetQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: agentKeys.fleet(co),
    queryFn: () => api.client.monitoring.fleet({ params: { co } }),
    refetchInterval: 60_000,
  });

export type BudgetIncidentFilter = {
  de?: string | undefined;
  period?: string | undefined;
  state: "open" | "resolved" | "all";
};

/** MONITORING-P2: the company's budget incidents (managers), newest first. */
export const budgetIncidentsQuery = (api: Api, co: string, filter: BudgetIncidentFilter) =>
  queryOptions({
    queryKey: agentKeys.budgetIncidents(co, filter),
    queryFn: () =>
      api.client.monitoring.budgetIncidents({
        params: { co },
        query: {
          state: filter.state,
          limit: 500,
          ...(filter.de ? { de_membership_id: filter.de } : {}),
          ...(filter.period ? { period_key: filter.period } : {}),
        },
      }),
  });

/** After an alert or stop write: the alerts lists, the fleet and every run's telemetry. */
export function invalidateMonitoring(qc: QueryClient, co: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: agentKeys.alerts(co) }),
    qc.invalidateQueries({ queryKey: agentKeys.fleet(co) }),
    qc.invalidateQueries({ queryKey: agentKeys.runs(co) }),
  ]).then(() => undefined);
}

/** Rows a page of a run's whole decision log (DC-07; the run detail inlines the first 100). */
export const DECISIONS_PAGE = 100;

/**
 * AGT-UI-2: a run's whole decision log in `seq` order, paged (`runs.decisions`). Only for a log
 * longer than the run detail carries: the screen enables it when the reader asks for it.
 */
export const runDecisionsQuery = (api: Api, co: string, id: string) =>
  infiniteQueryOptions({
    queryKey: agentKeys.runDecisions(co, id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.runs.decisions({
        params: { co, id },
        query: { limit: DECISIONS_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: next,
  });

/** Months of a DE's verified rate the profile shows, newest last. */
export const VERIFIED_RATE_MONTHS = 3;

/**
 * AGT-UI-2: a DE's `de.verified_rate` per UTC month (DC-07, ADR-0019 §6). Null for a reader who
 * may not view the DE's runs (404 / 403): the profile leaves the section out, without a toast.
 */
export const verifiedRateQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.deVerifiedRate(co, id),
    queryFn: async () => {
      try {
        return await api.client.digitalEmployees.verifiedRate({
          params: { co, id },
          query: { months: VERIFIED_RATE_MONTHS },
        });
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null;
        throw e;
      }
    },
  });

export const approvalsQuery = (api: Api, co: string, filter: ApprovalFilter) =>
  infiniteQueryOptions({
    queryKey: agentKeys.approvalList(co, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      pendingAsNull(() =>
        api.client.approvals.list({
          params: { co },
          query: {
            limit: AGENTS_PAGE,
            ...(filter.state ? { state: filter.state } : {}),
            ...(filter.approver ? { approver: filter.approver } : {}),
            ...(filter.kind ? { kind: filter.kind } : {}),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => (last ? next(last) : undefined),
    retry: false,
  });

export const approvalQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.approval(co, id),
    queryFn: () => api.client.approvals.get({ params: { co, id } }),
    retry: false,
  });

/**
 * AGT-UI-2: how many decisions wait for the caller (`my/approvals/count`), the Agents sidebar's
 * badge on every Agents page. Under the approvals key, so a decision anywhere (or a change ping)
 * recounts. Null while the route is not registered on an API (the badge is then hidden).
 */
export const myApprovalsCountQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: agentKeys.myApprovalsCount(co),
    queryFn: () => pendingAsNull(() => api.client.my.approvalsCount({ params: { co } })),
    retry: false,
    staleTime: 30_000,
  });

/** The caller's pending decisions (the roster's "Waiting for you", inbox rows, chat cards). */
export const myApprovalsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: agentKeys.myApprovals(co),
    queryFn: () =>
      pendingAsNull(() => api.client.my.approvals({ params: { co }, query: { limit: 50 } })),
    retry: false,
    // The roster, the inbox and chat cards share it; a mounting screen does not refetch it.
    staleTime: 30_000,
  });

export const budgetsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: agentKeys.budgetList(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      pendingAsNull(() =>
        api.client.budgets.list({
          params: { co },
          query: {
            limit: AGENTS_PAGE,
            state: "active",
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => (last ? next(last) : undefined),
    retry: false,
  });

export const budgetQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.budget(co, id),
    queryFn: () => api.client.budgets.get({ params: { co, id } }),
    retry: false,
  });

export const deBudgetQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.deBudget(co, id),
    queryFn: () => pendingAsNull(() => api.client.digitalEmployees.budget({ params: { co, id } })),
    retry: false,
  });

export const deAgentsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: agentKeys.deAgents(co, id),
    queryFn: () => api.client.digitalEmployees.agents({ params: { co, id } }),
  });

/** What the wake-up screen lists from the event feed: wake-ups, coalescing and refusals. */
export const WAKEUP_EVENT_TYPES = [
  "wakeup.requested",
  "wakeup.coalesced",
  "wakeup.dropped",
  "budget.reservation_refused",
  // DC-08: an ephemeral worker admission refused (no verbatim standing orders, depth, budget).
  "run.worker_refused",
] as const;

/**
 * The wake-up activity, oldest first (the feed is a `seq` cursor). Every page is followed so the
 * screen can show the newest first: wake-up events are few per agent per day, and `limit` is the
 * feed's maximum.
 */
export const wakeupEventsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: agentKeys.wakeups(co),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.client.events.list({
        params: { co },
        query: { types: [...WAKEUP_EVENT_TYPES], limit: 200, after_seq: pageParam },
      }),
    getNextPageParam: (last) =>
      last.data.length === 200 && last.next_cursor ? Number(last.next_cursor) : undefined,
  });

/**
 * WF-FIX (2026-10-26): the wake-ups waiting for self-hosted DEs to pull them, per DE
 * (`wakeups/inbox`, managers). Under the wake-ups key, so refreshing the feed recounts it.
 */
export const wakeupInboxQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: agentKeys.wakeupInbox(co),
    queryFn: () => api.client.wakeups.inbox({ params: { co } }),
  });

/**
 * AGT-UI-2: after an approval decision or a run's resume: every place that shows the approval,
 * its run, its chat card (`["company", co, "approval", id]`) or its inbox item.
 */
export function invalidateDecided(qc: QueryClient, co: string): Promise<void> {
  return Promise.all([
    invalidateAgents(qc, co),
    qc.invalidateQueries({ queryKey: ["company", co, "approval"] }),
    qc.invalidateQueries({ queryKey: ["company", co, "work", "inbox"] }),
  ]).then(() => undefined);
}

/** After a run, approval or budget write: what every Agents screen shows of it. */
export function invalidateAgents(qc: QueryClient, co: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: agentKeys.runs(co) }),
    qc.invalidateQueries({ queryKey: agentKeys.approvals(co) }),
    qc.invalidateQueries({ queryKey: agentKeys.budgets(co) }),
    qc.invalidateQueries({ queryKey: ["company", co, "de"] }),
  ]).then(() => undefined);
}
